/* eslint-disable react-hooks/exhaustive-deps */
/**
 * @name Tabs Component
 * @description A responsive tab navigation component that displays areas from a location and their exhibits
 *
 * @features
 * - Horizontal swipeable tabs with smooth scrolling using react-id-swiper
 * - Dropdown mega menu showing exhibits when hovering over tabs (appears after 500ms delay)
 * - Navigation arrows for horizontal scrolling when tabs overflow container
 * - Sticky header functionality that fixes tabs to top when scrolling past a certain point
 * - Automatic selection of first exhibit when switching areas
 * - Support for multiple languages with fallback to default language
 * - Responsive design with touch/swipe support
 *
 * @implementation
 * - Uses AppContext to access location data, selected area/exhibit IDs, and related methods
 * - Builds tab items from location's areaOrder with translated names
 * - Implements custom swiper navigation with isBeginning/isEnd state tracking
 * - Handles mouse interactions for showing/hiding the mega menu with timeout
 * - Implements scroll event listener to control the sticky behavior
 * - Auto-selects exhibits when changing areas for continuous navigation flow
 *
 * @dependencies
 * - react-id-swiper: For touch-enabled tab swiping
 * - react-icons: For navigation arrows (AiOutlineArrowLeft, AiOutlineArrowRight)
 * - AppContext: For location data, selected IDs, and navigation methods
 * - styled-components: For themed styling
 *
 * @usage
 * Simply include the Tabs component in your application. It automatically
 * pulls data from AppContext and renders the appropriate navigation based
 * on the current location and selected area/exhibit.
 *
 * @note
 * - The component uses a 500ms hover delay before showing the mega menu
 * - Tabs with no exhibits will not be displayed
 * - Sticky behavior depends on scroll position and dynamically adapts
 */

import React, { FC, useEffect, useRef, useState } from "react";
import { AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";
import Swiper from "react-id-swiper";
import { useAppContext } from "src/context/AppContext";
import { theme } from "src/helpers/theme";
import utility from "src/helpers/utility";
import { Exhibit, Location } from "src/types";
import {
  MainTabMenuWrapper,
  MegaMenu,
  MegaMenuInnerContainer,
  MegaMenuItem,
  SwiperOutWrapper,
  Tab,
  TabArrowWrapper,
  TabWrapper,
} from "./styled";
import urls from "src/helpers/urls";
import { ViewsByTypes } from "src/api/analytic";
import { useNavigate } from "react-router";

const TIMEOUT = 500;

const Tabs: FC = () => {
  // Retrieve all needed data from context
  const {
    selectedLanguage,
    getLocation,
    getVisibleAreas,
    selectedExhibitId,
    selectedAreaId,
    setSelectedAreaId,
    setSelectedExhibitId,
    getVisibleExhibitsByAreaId,
    getVisibleExhibits,
  } = useAppContext();

  const location: Location = getLocation() || ({} as Location);
  const defaultLanguage = location.defaultLanguage || "en";
  const visibleAreas = getVisibleAreas();
  const navigate = useNavigate();

  // Build tabItems from visibleAreas
  const tabItems = visibleAreas.map((area, index) => ({
    label: utility.getName(area.name, selectedLanguage, defaultLanguage),
    key: area.id,
    index,
    exhibitsLength: getVisibleExhibitsByAreaId(area.id).length,
  }));

  // Determine current tab index based on selectedAreaId
  // const currentIndex = visibleAreas.findIndex(
  //   a => a.id === (location.areaOrder && a.id === a.id && a.id === a.id)
  // );
  // Instead, use selectedAreaId from context:
  const activeIndex = visibleAreas.findIndex((a) => a.id === selectedAreaId);

  const [internalIndex, setInternalIndex] = useState(activeIndex);
  const sliderRef = useRef(null);
  const [isFixed, setIsFixed] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState(false);
  let flagPoint = 0;

  // Get exhibits from the currently selected area
  const currentAreaId = visibleAreas[internalIndex]?.id;
  const exhibits: Exhibit[] = currentAreaId
    ? getVisibleExhibits(selectedExhibitId)
    : [];

  const [isEnd, setIsEnd] = useState<boolean>(false);
  const [isBeginning, setIsBeginning] = useState<boolean>(false);

  // Remove auto-slideTo on every update, only scroll to tab on click
  // useEffect(() => {
  //   if (sliderRef && sliderRef.current.swiper.slides[internalIndex]) {
  //     sliderRef.current.swiper.slideTo(internalIndex, 300, false);
  //     updateStartEndOfTab();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [sliderRef, internalIndex]);

  // Only scroll to tab when user clicks a tab
  const handleTabClick = (tabIndex, tabKey) => {
    setInternalIndex(tabIndex);
    setSelectedAreaId(tabKey);
    const exhibitsInArea = getVisibleExhibitsByAreaId(tabKey);
    if (exhibitsInArea.length > 0) {
      setSelectedExhibitId(exhibitsInArea[0].id);
      navigate(urls.exhibit(exhibitsInArea[0].id, ViewsByTypes.menu));
    }
    if (sliderRef.current && sliderRef.current.swiper) {
      sliderRef.current.swiper.slideTo(tabIndex, 300, false);
      updateStartEndOfTab();
    }
  };

  const updateStartEndOfTab = () => {
    if (sliderRef.current.swiper) {
      setIsBeginning(sliderRef.current.swiper.isBeginning);
      setIsEnd(sliderRef.current.swiper.isEnd);
    }
  };

  const goNext = () => {
    if (sliderRef.current.swiper) {
      sliderRef.current.swiper.slideNext();
      updateStartEndOfTab();
    }
  };

  const goPrev = () => {
    if (sliderRef.current.swiper) {
      sliderRef.current.swiper.slidePrev();
      updateStartEndOfTab();
    }
  };

  let timer: any;
  const mouseEnter = () => {
    timer = setTimeout(() => {
      setShowMenu(true);
    }, TIMEOUT);
  };

  const mouseLeave = () => {
    setShowMenu(false);
    clearTimeout(timer);
  };

  document.addEventListener("scroll", () => {
    const tab = document.getElementById("tabline");
    const space = tab && tab.offsetTop;
    if (window.scrollY > space && window.scrollY > flagPoint) {
      setIsFixed(true);
    } else if (window.scrollY < flagPoint) {
      setIsFixed(false);
    } else {
      flagPoint = space > 0 ? space : flagPoint;
      setIsFixed(false);
    }
  });

  useEffect(() => {
    // Find the index of the area that matches the selectedAreaId
    const newIndex = visibleAreas.findIndex(
      (area) => area.id === selectedAreaId
    );
    if (newIndex !== -1 && newIndex !== internalIndex) {
      setInternalIndex(newIndex);
    }
  }, [selectedAreaId, visibleAreas]);

  return (
    <MainTabMenuWrapper
      as="div"
      id="tabline"
      $isfixed={isFixed ? "true" : "false"}
      onMouseLeave={mouseLeave}
    >
      <TabWrapper>
        <TabArrowWrapper>
          {!isBeginning && (
            <AiOutlineArrowLeft
              color={theme.primary}
              size={21}
              onClick={goPrev}
            />
          )}
        </TabArrowWrapper>
        <SwiperOutWrapper>
          <Swiper
            {...{
              slidesPerView: "auto",
              freeMode: true,
              freeModeSticky: true,
              freeModeMomentumRatio: 0.25,
              freeModeMomentumVelocityRatio: 0.5,
              slideToClickedSlide: true,
              on: {
                slideChange: () => {
                  if (sliderRef.current.swiper) {
                    updateStartEndOfTab();
                  }
                },
              },
            }}
            ref={sliderRef}
          >
            {tabItems.map((tab, tabIndex) => (
              <Tab
                as="span"
                key={tab.key}
                onMouseEnter={mouseEnter}
                onClick={() => handleTabClick(tabIndex, tab.key)}
                $isselected={tab.key === selectedAreaId ? "true" : "false"}
                style={{ display: tab.exhibitsLength ? "block" : "none" }}
              >
                {tab.label}
              </Tab>
            ))}
          </Swiper>
        </SwiperOutWrapper>
        <TabArrowWrapper>
          {!isEnd && (
            <AiOutlineArrowRight
              color={theme.primary}
              size={21}
              onClick={goNext}
            />
          )}
        </TabArrowWrapper>
      </TabWrapper>

      {showMenu && (
        <MegaMenu>
          <MegaMenuInnerContainer>
            {exhibits.map((item, itemIndex) => {
              const exhibitName = utility.getName(
                item.name,
                selectedLanguage,
                defaultLanguage
              );
              return (
                <MegaMenuItem
                  key={itemIndex}
                  className={item.id === selectedExhibitId ? "active" : ""}
                  title={exhibitName}
                  exhibitid={item.id}
                />
              );
            })}
          </MegaMenuInnerContainer>
        </MegaMenu>
      )}
    </MainTabMenuWrapper>
  );
};

export default Tabs;
